import { normalizeArtifactRef } from 'flare-chat-core/contracts/context/artifact-ref';

const text = value => typeof value === 'string' && value.trim().length > 0;
const array = value => Array.isArray(value) ? value : [];

/** Translate FLARE EvidenceReadingTarget coordinates; never infer offsets or source identity. */
export const citationCoordinate = target => {
  if (!target || ![target.run_id, target.source_id, target.evidence_id].every(text)
    || ![target.record_id, target.chunk_id].every(value => typeof value === 'string')
    || Boolean(target.artifact_ref) === Boolean(target.source_ref)) return '';
  if (target.source_ref) {
    const source = target.source_ref;
    if (![source.project_id, source.source_id, source.reference_id, source.extraction_artifact_id, target.source_excerpt].every(text)
      || !/^[a-f0-9]{64}$/.test(source.file_sha256) || !/^sha256:[a-f0-9]{64}$/.test(source.text_digest)
      || target.source_id !== source.source_id || target.evidence_id !== source.reference_id
      || target.record_id !== '' || target.chunk_id !== '') return '';
    return JSON.stringify(['source', source.project_id, source.source_id, source.reference_id, source.file_sha256,
      source.extraction_artifact_id, source.text_digest, target.run_id, target.evidence_id, target.source_excerpt]);
  }
  try {
    const ref = normalizeArtifactRef(target.artifact_ref);
    return JSON.stringify(['artifact', ref.scope.tenant_id, ref.scope.project_id, ref.artifact_id,
      ref.artifact_type, ref.artifact_version_id, ref.content_digest, target.run_id, target.source_id,
      target.record_id, target.chunk_id, target.evidence_id]);
  } catch { return ''; }
};

export const isPreparedCitation = citation => {
  const coordinate = citationCoordinate(citation?.citation_target);
  return Boolean(coordinate) && text(citation?.citation_id) && text(citation?.citation_anchor_id)
    && citation.evidence_admission?.citation_eligible === true
    && citationCoordinate(citation.reading?.target) === coordinate;
};

export const resolveCitationBinding = (payload, id) => {
  const matches = array(payload?.citations).filter(row => row?.citation_id === id);
  return matches.length === 1 && isPreparedCitation(matches[0]) ? matches[0] : null;
};

/** One source-content fence prepares both renderers and the separate citation block. */
export const bindCitationPresentation = (content, payload) => {
  const source = String(content ?? '');
  const projection = payload?.conversation_projection;
  const bound = projection?.source_content === source && typeof projection.content === 'string';
  const inlineIds = new Set(bound ? array(projection.inline_citation_ids)
    .filter(id => resolveCitationBinding(payload, id)) : []);
  const remaining = array(payload?.citations).filter(row => !inlineIds.has(row.citation_id));
  return { content: bound ? projection.content : source.trim(), citationPayload: bound ? payload : null,
    remainingPayload: remaining.length ? { ...payload, citations: remaining } : null };
};

/** Public UI action translation retains the backend's full target and anchor unchanged. */
export const citationAction = citation => ({ type: 'open_evidence_citation', citation_id: citation.citation_id,
  citation_target: citation.citation_target, evidence_anchor_id: citation.citation_anchor_id });
